import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'
import {
  Overlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  ModalIcon,
  ModalBody,
  ModalFooter,
  Button,
} from './styled'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm?: () => void
  title: string
  message: string
  type?: 'info' | 'error' | 'success' | 'warning' | 'confirm'
  confirmText?: string
  cancelText?: string
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'info',
  confirmText = 'OK',
  cancelText = 'Cancel',
}) => {
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden'

      // Handle ESC key
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose()
        }
      }
      document.addEventListener('keydown', handleEsc)

      return () => {
        document.body.style.overflow = ''
        document.removeEventListener('keydown', handleEsc)
      }
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  const getIcon = () => {
    switch (type) {
      case 'error':
        return '❌'
      case 'success':
        return '✅'
      case 'warning':
        return '⚠️'
      case 'confirm':
        return '❓'
      default:
        return 'ℹ️'
    }
  }

  const modalContent = (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader $type={type}>
          <ModalTitle $type={type}>
            <ModalIcon>{getIcon()}</ModalIcon>
            {title}
          </ModalTitle>
        </ModalHeader>

        <ModalBody>
          {message}
        </ModalBody>

        <ModalFooter>
          {type === 'confirm' ? (
            <>
              <Button $variant="secondary" onClick={onClose}>
                {cancelText}
              </Button>
              <Button $variant="primary" onClick={() => {
                onConfirm?.()
                onClose()
              }}>
                {confirmText}
              </Button>
            </>
          ) : (
            <Button
              $variant={type === 'error' ? 'danger' : 'primary'}
              onClick={onClose}
            >
              {confirmText}
            </Button>
          )}
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  )

  // Portal to render modal at document root
  return ReactDOM.createPortal(modalContent, document.body)
}

export { Modal }